/**
 * Iter269.20k — Subprocessor disclosure section for /trust
 *
 * Pulls /api/public/subprocessors and renders the canonical list +
 * RSS-feed link. Standard SOC 2 / GDPR Article 28 / DPA disclosure.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Rss, ExternalLink, ShieldCheck, Loader2 } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function TrustSubprocessorsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/subprocessors`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message || "load failed"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section id="subprocessors" className="my-12" data-testid="trust-subprocessors-loading">
        <div className="flex items-center gap-2 text-xs qt-mono" style={{ color: "var(--qt-text-3)" }}>
          <Loader2 size={14} className="animate-spin" /> Loading subprocessor list…
        </div>
      </section>
    );
  }
  if (err || !data) return null;

  return (
    <section id="subprocessors" className="my-16 scroll-mt-24" data-testid="trust-subprocessors-section">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="qt-mono text-[10px] mb-2"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
            SOC 2 · GDPR Article 28 · DPA
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Chivo, sans-serif", letterSpacing: "-0.02em" }}>
            Our Subprocessors
          </h2>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            Third-party services that process customer data on QorTrace's
            behalf. We disclose every active relationship below + publish
            a change-log so your security team can subscribe.
          </p>
        </div>
        <a
          href={`${API}/api/public/subprocessors.rss`}
          target="_blank" rel="noopener noreferrer"
          data-testid="trust-subprocessors-rss"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs qt-mono"
          style={{
            background:  "rgba(255,140,0,0.08)",
            border:      "1px solid rgba(255,140,0,0.35)",
            color:       "#FFB000",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          <Rss size={12} /> Subscribe · RSS feed
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="trust-subprocessors-grid">
        {(data.active || []).map((s) => (
          <article
            key={s.id}
            data-testid={`trust-subproc-${s.id}`}
            className="qt-card p-5 flex flex-col gap-3"
            style={{
              background:  "rgba(4,9,20,0.6)",
              borderColor: "var(--qt-border-soft)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold" style={{ fontFamily: "Chivo, sans-serif", color: "#FFFFFF" }}>
                  {s.name}
                </h3>
                <div className="text-[11px] qt-mono mt-1" style={{ color: "var(--qt-text-3)" }}>
                  {s.location || "—"}
                </div>
              </div>
              {s.dpa_url && (
                <a
                  href={s.dpa_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`trust-subproc-${s.id}-dpa`}
                  className="inline-flex items-center gap-1 text-xs qt-mono px-2 py-1 rounded shrink-0"
                  style={{
                    background:  "rgba(0,229,255,0.08)",
                    border:      "1px solid rgba(0,229,255,0.3)",
                    color:       "var(--qt-cyan)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  DPA <ExternalLink size={10} />
                </a>
              )}
            </div>
            <p className="text-sm" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
              {s.purpose}
            </p>
            {s.data_categories && s.data_categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {s.data_categories.map((c) => (
                  <span
                    key={c}
                    className="qt-mono text-[10px] px-2 py-1 rounded"
                    style={{
                      background:  "rgba(255,255,255,0.04)",
                      border:      "1px solid rgba(255,255,255,0.08)",
                      color:       "var(--qt-text-3)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
            {s.notes && (
              <div className="text-xs mt-1 italic"
                   style={{ color: "var(--qt-text-3)", borderLeft: "2px solid rgba(31,221,131,0.4)", paddingLeft: 10 }}>
                {s.notes}
              </div>
            )}
          </article>
        ))}
      </div>

      {data.retired && data.retired.length > 0 && (
        <div className="mt-10" data-testid="trust-subprocessors-retired">
          <div className="qt-mono text-[10px] mb-3"
               style={{ color: "var(--qt-text-3)", letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Recently retired (last 180 days)
          </div>
          <ul className="space-y-1.5 text-sm" style={{ color: "var(--qt-text-3)" }}>
            {data.retired.map((s) => (
              <li key={s.id} className="qt-mono text-xs">
                <span className="text-slate-500 mr-2">×</span>
                {s.name}
                <span className="ml-2 text-slate-600">
                  · retired {s.removed_at?.slice(0, 10) || "?"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 text-xs flex items-center gap-2"
           style={{ color: "var(--qt-text-3)" }}>
        <ShieldCheck size={12} className="text-emerald-400/70" />
        <span>
          Total: <strong className="text-white">{data.active_count}</strong>{" "}
          active subprocessor{data.active_count !== 1 ? "s" : ""}.
          Updated automatically when changes happen.
        </span>
      </div>
    </section>
  );
}
